import React from 'react'
import { Typography, Card, Image } from 'antd'
import SimpleBackground from '@/components/SimpleBackground'
import Link from 'next/link'

const SERIF = '"Noto Serif SC","Source Han Serif SC","Songti SC",serif'

// 图（带圆角+投影框 + 居中图注）
const Fig = ({ src, no, cap }: { src: string; no: string; cap: string }) => (
  <figure className="my-6 text-center">
    <span className="inline-block max-w-full overflow-hidden rounded-xl border border-gray-200 shadow-[0_10px_28px_rgba(24,20,16,0.14)]">
      <Image src={src} alt={cap} />
    </span>
    <figcaption className="mt-2.5 text-[13px] tracking-wide text-gray-500">
      <b className="text-ink">图 {no}</b>　{cap}
    </figcaption>
  </figure>
)

// 操作步骤（红色圆序号）
const Steps = ({ items }: { items: React.ReactNode[] }) => (
  <ol className="my-3 list-none space-y-2.5 pl-0">
    {items.map((t, i) => (
      <li key={+i} className="relative pl-8 text-[15.5px] leading-8">
        <span className="absolute left-0 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-brand text-xs font-bold leading-none text-white">
          {i + 1}
        </span>
        <span>{t}</span>
      </li>
    ))}
  </ol>
)

// 提示 / 注意（金色）
const Note = ({ children }: { children: React.ReactNode }) => (
  <div className="my-4 rounded-lg border border-[#f0dcc0] border-l-4 border-l-gold bg-[#fdf6ee] px-4 py-3 text-sm leading-7 text-[#5a4a2e]">
    {children}
  </div>
)

const Chapter = ({ no, title, id }: { no: string; title: string; id: string }) => (
  <div id={id} className="mt-14 scroll-mt-6 border-t border-gray-200 pt-7">
    <div className="text-sm font-black tracking-[0.3em] text-gold" style={{ fontFamily: SERIF }}>
      {no}
    </div>
    <h2 className="mt-1.5 border-b-2 border-ink pb-3 text-[26px] font-black leading-tight" style={{ fontFamily: SERIF }}>
      {title}
    </h2>
  </div>
)

const Sec = ({ n, title, id }: { n: string; title: string; id?: string }) => (
  <h3 id={id} className="mt-8 scroll-mt-6 text-xl font-bold" style={{ fontFamily: SERIF }}>
    <span className="mr-2 text-brand">{n}</span>
    {title}
  </h3>
)

const Sub = ({ n, title }: { n: string; title: string }) => (
  <h4 className="mt-5 text-[15.5px] font-bold text-[#2a2621]">
    <span className="mr-1.5 text-brand">{n}</span>
    {title}
  </h4>
)

const P = ({ children }: { children: React.ReactNode }) => (
  <p className="my-3 text-justify text-[15.5px] leading-8 text-[#241f1a]">{children}</p>
)

const TocRow = ({ href, label, dot = true }: { href: string; label: string; dot?: boolean }) => (
  <li className={dot ? 'my-1.5 font-normal text-[#514c46]' : 'my-2 font-bold'}>
    <a href={href} className="flex items-baseline text-inherit no-underline hover:text-brand">
      <span>{label}</span>
    </a>
  </li>
)

const UserManual = () => {
  const { Title } = Typography

  return (
    <div className="m-auto h-full w-full">
      <SimpleBackground />
      <div className="m-auto w-[820px] max-w-full overflow-auto px-2 py-12 lg:px-6">
        <Card bordered={false} className="w-full bg-white shadow-lg lg:p-8">
          {/* ── 报头 masthead ── */}
          <div className="border-b-[3px] border-double border-ink pb-7 text-center">
            <div className="text-[13px] font-bold tracking-[0.3em] text-brand">灵缇互娱 · 掼牌赛事系统</div>
            <Title className="!mb-1 !mt-3.5" level={1} style={{ fontFamily: SERIF, fontWeight: 900 }}>
              产品使用手册
            </Title>
            <div className="text-[13px] tracking-wide text-gray-500">Version 2.0 · 2026 修订版</div>
          </div>

          {/* ── 目录 ── */}
          <div className="my-8 rounded-xl border border-gray-200 bg-[#faf8f5] px-6 py-5">
            <p className="mb-3 text-base font-extrabold tracking-[0.2em]" style={{ fontFamily: SERIF }}>
              目　录
            </p>
            <ol className="list-none pl-0 text-[14.5px]">
              <TocRow href="#ch1" label="第一章　编排员操作指南" dot={false} />
              <ol className="list-none pl-6">
                <TocRow href="#s1-1" label="1.1　账号注册与登录" />
                <TocRow href="#s1-2" label="1.2　创建比赛" />
                <TocRow href="#s1-3" label="1.3　设定规则" />
                <TocRow href="#s1-4" label="1.4　选手录入" />
                <TocRow href="#s1-5" label="1.5　名单核对" />
                <TocRow href="#s1-6" label="1.6　桌次编排" />
                <TocRow href="#s1-7" label="1.7　比赛开始" />
                <TocRow href="#s1-8" label="1.8　成绩录入" />
                <TocRow href="#s1-9" label="1.9　统计排名" />
              </ol>
              <TocRow href="#ch2" label="第二章　选手参赛指南" dot={false} />
              <ol className="list-none pl-6">
                <TocRow href="#s2-1" label="2.1　基本流程" />
                <TocRow href="#s2-2" label="2.2　报名" />
                <TocRow href="#s2-3" label="2.3　签到" />
                <TocRow href="#s2-4" label="2.4　成绩录入与查询" />
              </ol>
              <TocRow href="#ch3" label="第三章　复式发牌机" dot={false} />
              <TocRow href="#appendix" label="附录　常见问题与联系方式" dot={false} />
            </ol>
          </div>

          {/* ══ 第一章 ══ */}
          <Chapter no="第 一 章" title="编排员操作指南" id="ch1" />
          <P>
            本章面向赛事编排员，按照一场比赛从无到有的完整生命周期，逐节讲解系统操作。整体流程为：
            <b>创建比赛 → 设定规则 → 选手录入 → 名单核对 → 桌次编排 → 比赛开始 → 成绩录入 → 统计排名</b>
            。各节均配以操作截图，图中关键入口以序号标注。
          </P>

          <Sec n="1.1" title="账号注册与登录" id="s1-1" />
          <P>
            登录灵缇互娱掼牌平台官方网站（
            <a href="https://hy.lingti.com/" target="_blank" rel="noreferrer" className="text-[#576b95]">
              hy.lingti.com
            </a>
            ），点击右上角「登录」，完成新账号注册或使用已有账号登录。
          </P>
          <Fig src="/manual/1.1.png" no="1-1" cap="首页右上角登录 / 注册入口" />
          <P>
            深入阅读：
            <Link href="/manual/register_and_login" className="text-[#576b95]">
              注册及登录详解
            </Link>
          </P>

          <Sec n="1.2" title="创建比赛" id="s1-2" />
          <P>完成登录后即可创建一场赛事。系统支持球拍类与棋牌类的全部赛制，创建时选择对应类型即可，后续设置项会随赛制自动调整。</P>
          <Sub n="1.2.1" title="操作步骤" />
          <Steps
            items={[
              <>
                在首页点击 <b>「立即办赛」</b>，进入创建入口。
              </>,
              <>
                依次录入 <b>比赛名称</b>、选择 <b>比赛类型</b>（赛制）。
              </>,
              <>
                按需勾选 <b>「是否为测试赛」</b>（测试赛便于演练，不占用创建额度）。
              </>,
              <>
                点击 <b>「提交」</b>，赛事创建完成，自动进入比赛设置页。
              </>,
            ]}
          />
          <Fig src="/manual/1.2.png" no="1-2" cap="创建比赛：填写比赛名与类型、选择是否测试赛后提交" />
          <Note>
            <b>提示　</b>比赛类型创建后不可更改；如选错，请删除该赛事后重新创建。是否测试赛可在「比赛设置 → VIP 专区」内更改。
          </Note>
          <P>
            深入阅读：
            <Link href="/manual/create_match" className="text-[#576b95]">
              创建比赛详解（各赛制说明）
            </Link>
          </P>

          <Sec n="1.3" title="设定规则" id="s1-3" />
          <P>根据界面提示设定比赛规则（计分方式、轮次、赛制参数等），完成后点击保存。</P>
          <Fig src="/manual/1.3.png" no="1-3" cap="比赛规则设定界面" />
          <P>
            深入阅读：
            <Link href="/manual/set_rules" className="text-[#576b95]">
              设定规则详解
            </Link>
          </P>

          <Sec n="1.4" title="选手录入" id="s1-4" />
          <P>点击「队伍管理」进入选手信息录入界面。参赛选手信息录入有三种方式：</P>
          <Steps
            items={[
              <>
                点击 <b>「手动新增一队」</b>，按提示录入选手信息。
              </>,
              <>
                点击 <b>「批量导入队伍」</b>，选择事先编辑好的 Excel 表格导入。不同赛制表格格式不同，样表可通过「操作 → 导出样例表」获取。
              </>,
              <>
                点击 <b>「操作」</b>，选择相应菜单录入选手信息。
              </>,
            ]}
          />
          <Fig src="/manual/1.4.png" no="1-4" cap="队伍管理：手动新增 / 批量导入 / 操作菜单" />
          <P>
            深入阅读：
            <Link href="/manual/input_players" className="text-[#576b95]">
              选手录入详解
            </Link>
          </P>

          <Sec n="1.5" title="名单核对" id="s1-5" />
          <Sub n="1.5.1" title="校对名单" />
          <P>
            点击「队伍管理」对选手名单进行校对。点击队伍后的「编辑」可修改选手信息；不需要编排的选手，在编辑界面取消「是否签到」。点击「删除」可删除重复或不参赛的队伍（如非必要请谨慎删除）。
          </P>
          <Fig src="/manual/1.5.1.png" no="1-5" cap="队伍编辑：修改信息、控制签到状态" />
          <Fig src="/manual/1.5.2.png" no="1-6" cap="删除重复 / 不参赛队伍" />
          <Sub n="1.5.2" title="完成签到" />
          <P>选手用微信扫描「签到 / 记分二维码」自行签到：进入签到系统、登录后点击「签」即可。</P>
          <Fig src="/manual/1.5.3.png" no="1-7" cap="选手扫码自助签到" />
          <P>也可由编排员在「队伍管理 → 操作 → 一键签到」统一签到。只有签到成功的选手才会进入编排。</P>
          <Fig src="/manual/1.5.4.png" no="1-8" cap="编排员一键签到" />
          <P>
            深入阅读：
            <Link href="/manual/check_roster" className="text-[#576b95]">
              名单核对详解
            </Link>
          </P>

          <Sec n="1.6" title="桌次编排" id="s1-6" />
          <Sub n="1.6.1" title="创建桌次" />
          <P>编排前必须先创建桌次。点击「桌次编排 → 操作 → 批量创建桌次」，根据实际桌数填入数量后确定；系统会依据参赛人数提供默认桌数。</P>
          <Fig src="/manual/1.6.1.png" no="1-9" cap="批量创建桌次" />
          <Sub n="1.6.2" title="自动编排" />
          <P>点击「操作 → 一键编排」，根据提示选择轮次和其他条件后确定，系统自动完成编排。</P>
          <Fig src="/manual/1.6.2.png" no="1-10" cap="一键自动编排" />
          <Note>
            <b>注意　</b>如无特殊情况，编排完成后请勿删除桌次，尤其是开赛后，以免造成成绩统计错误。有特殊需要请联系灵缇互娱工作人员。
          </Note>
          <Sub n="1.6.3" title="交换座次 / 手动安排" />
          <P>「操作 → 交换座次」用于两支队伍互换位置；「操作 → 手动安排」用于人工指定桌次、1 号队、2 号队与轮次。</P>
          <Fig src="/manual/1.6.3.png" no="1-11" cap="交换座次" />
          <Fig src="/manual/1.6.4.png" no="1-12" cap="手动安排桌次" />
          <P>
            深入阅读：
            <Link href="/manual/arrange_table" className="text-[#576b95]">
              桌次编排详解
            </Link>
          </P>

          <Sec n="1.7" title="比赛开始" id="s1-7" />
          <Sub n="1.7.1" title="比赛计时" />
          <P>宣布开始时立即点击该轮的「比赛计时 → 开始」，系统自动倒计时。需要重计点「重新计时」；本轮结束点「终止」，进入下一轮。</P>
          <Fig src="/manual/1.7.1.png" no="1-13" cap="开始本轮计时" />
          <Fig src="/manual/1.7.2.png" no="1-14" cap="重新计时 / 终止本轮" />
          <Sub n="1.7.2" title="比赛判罚" />
          <P>选手违规时点击「比赛判罚 → 判罚」，依次选择罚分、分数类型、队伍并录入原因后确定。分数类型包括：场分、胜轮次、级差分、升级数、对手级差分。系统会在统计时自动扣除。</P>
          <Fig src="/manual/1.7.3.png" no="1-15" cap="录入判罚" />
          <Sub n="1.7.3" title="投屏控制" />
          <P>点击「投屏控制 → 开始投屏」，将选定内容投屏，可随时调整投屏内容及其位置、大小、滚动速度等参数。</P>
          <Fig src="/manual/1.7.4.png" no="1-16" cap="投屏控制面板" />
          <P>
            深入阅读：
            <Link href="/manual/start_match" className="text-[#576b95]">
              比赛开始详解
            </Link>
          </P>

          <Sec n="1.8" title="成绩录入" id="s1-8" />
          <P>点击「桌次编排」，选择桌次进入，对该桌记分进行管理。</P>
          <Fig src="/manual/1.8.1.png" no="1-17" cap="进入桌次记分管理" />
          <Fig src="/manual/1.8.2.png" no="1-18" cap="记分明细" />
          <P>选择桌次点击「结算」对该桌成绩进行结算；结算后按钮变为「修改」，点击可对最终成绩重新修改。</P>
          <Fig src="/manual/1.8.3.png" no="1-19" cap="结算 / 修改本桌成绩" />
          <P>
            深入阅读：
            <Link href="/manual/input_score" className="text-[#576b95]">
              成绩录入详解
            </Link>
          </P>

          <Sec n="1.9" title="统计排名" id="s1-9" />
          <P>点击「成绩排名」可查看成绩与排名，并提供图片和表格两种形式供下载。</P>
          <Fig src="/manual/1.9.png" no="1-20" cap="成绩与排名统计" />
          <P>
            深入阅读：
            <Link href="/manual/ranking_stats" className="text-[#576b95]">
              统计排名详解
            </Link>
          </P>

          {/* ══ 第二章 ══ */}
          <Chapter no="第 二 章" title="选手参赛指南" id="ch2" />

          <Sec n="2.1" title="基本流程" id="s2-1" />
          <P>选手侧流程为：报名 → 签到 → 成绩录入 → 成绩查询。编排员创建赛事后，在「比赛设置 → 基础信息」可获得报名二维码、报名链接、签到 / 记分二维码，供选手使用。</P>
          <Fig src="/manual/2.1.png" no="2-1" cap="比赛设置中的报名 / 签到二维码与链接" />

          <Sec n="2.2" title="报名" id="s2-2" />
          <Steps
            items={[
              <>
                <b>二维码报名：</b>微信扫码报名二维码，点击进入报名，按提示录入信息后提交。
              </>,
              <>
                <b>链接报名：</b>打开报名链接，界面与流程同二维码报名。
              </>,
              <>
                <b>公众号报名：</b>关注《灵缇互娱》公众号后经公众号报名（需编排员在「比赛设置 → 赛事设置」勾选允许微信公众号报名）。
              </>,
            ]}
          />
          <Fig src="/manual/2.2.1.png" no="2-2" cap="扫码 / 链接报名界面" />
          <Fig src="/manual/2.2.2.png" no="2-3" cap="报名信息录入" />
          <Note>
            <b>注意　</b>报名时若同一队伍的 2 名选手与系统中已有队伍的 2 名选手重名，系统会提示不通过，需联系编排员录入。
          </Note>

          <Sec n="2.3" title="签到" id="s2-3" />
          <P>选手用微信扫描「签到 / 记分二维码」，输入姓名，点「查询」，再点「签」即可。若查询不到信息，请联系编排员。同一队伍只要有一名选手签到，另一名即为已签到状态。</P>
          <Fig src="/manual/2.3.1.png" no="2-4" cap="选手自助签到" />
          <P>签到完成且系统完成编排后，选手可在手机上立即看到自己的桌次和方位，请及时查看并就座。</P>
          <Fig src="/manual/2.3.2.png" no="2-5" cap="手机端查看桌次与方位" />

          <Sec n="2.4" title="成绩录入与查询" id="s2-4" />
          <Sub n="2.4.1" title="计分" />
          <P>选手扫码进入「记分 → 升级」，选择获胜方与正确的升级数后确认。不同赛制记分方式略有不同（复式对应级牌记分，单人赛对应个人记分），以界面显示为准，现场由裁判长或编排员讲解指导。</P>
          <Fig src="/manual/2.4.1.png" no="2-6" cap="选手扫码记分" />
          <Fig src="/manual/2.4.2.png" no="2-7" cap="选择获胜方与升级数" />
          <Sub n="2.4.2" title="撤销与提交" />
          <P>记分错误可点「撤销」，每点一次从后往前撤销一笔。本轮结束、双方确认无误后点「提交」锁定本轮成绩；若误提交，可上报由编排员取消提交、修正后重新提交。</P>
          <Note>
            <b>注意　</b>到达比赛时间后，编排员可按裁判长指令在后台「一键结算」，结算后方可开始下一轮编排。
          </Note>
          <Sub n="2.4.3" title="成绩查询" />
          <P>选手扫码后点击「每轮成绩」，可查看自己每一轮的比赛成绩。</P>
          <Fig src="/manual/2.4.3.png" no="2-8" cap="每轮成绩查询入口" />
          <Fig src="/manual/2.4.4.png" no="2-9" cap="选手个人成绩明细" />

          {/* ══ 第三章 ══ */}
          <Chapter no="第 三 章" title="复式发牌机" id="ch3" />
          <P>灵缇牌三代复式发牌机，是世界首款基于物联网、AI 及云边协同的轻便型复式发牌机（产地：中国·苏州），与灵缇赛事系统深度集成。</P>
          <Fig src="/fushiji/fapaiji.png" no="3-1" cap="灵缇牌三代复式发牌机" />
          <Sub n="3.1" title="核心特征" />
          <ul className="my-3 list-disc space-y-1 pl-6 text-[15px] leading-7 text-[#241f1a]">
            <li>
              <b>智能操控：</b>支持 Web 端、小程序、iOS、Android 等多设备操控
            </li>
            <li>
              <b>轻巧便携：</b>约 20cm³，重量 3–5 千克，只手轻松携带
            </li>
            <li>
              <b>AI 视觉识别：</b>支持各类主流牌种，包括异型牌、带广告牌
            </li>
            <li>
              <b>模块化设计：</b>可扩展 3–108 槽，耐用易维护
            </li>
            <li>
              <b>安全保密：</b>随机算子生成牌库，支持均衡牌力算法
            </li>
            <li>
              <b>系统联动：</b>与灵缇赛事系统集成，支持复盘、牌力测算
            </li>
            <li>
              <b>集群操控：</b>支持百台机器同时发牌，适用大型赛事
            </li>
          </ul>
          <Note>
            <b>惊爆价 9999 元　</b>另送灵缇赛事系统至尊 VIP 半年 + 一年维保服务。详见
            <Link href="/baike/fapaiji" className="ml-1 font-bold text-brand hover:text-brand-dark">
              发牌机详细说明书 →
            </Link>
          </Note>

          {/* ══ 附录 ══ */}
          <Chapter no="附 录" title="常见问题与联系方式" id="appendix" />
          <P>系统持续升级，界面以最新版本为准。使用过程中如有疑问，可联系灵缇互娱工作人员：臧子夜（15370036553）。</P>

          <div className="mt-10 border-t border-dashed border-gray-300 pt-4 text-center text-xs text-gray-400">
            © 北京灵缇互娱体育科技有限公司 · 产品使用手册 v2.0
          </div>
        </Card>
      </div>
    </div>
  )
}

Object.assign(UserManual as any, {
  title: '使用手册 · 如何在灵缇赛事系统创建与管理比赛',
  description: '一步步教您：注册登录、创建比赛、录入选手、编排桌次/场地、开始计时、录入比分、查看排名统计。覆盖球拍类与棋牌类全部赛制。',
})

export default UserManual
